/**
 * CVA style variants for `FileDropZone` (LF-4642).
 *
 * Tailwind + design tokens only. The drag-over active state is violet for
 * every variant (the strong "drop now" affordance); the file list itself
 * stays muted (see `file-drop-zone-item`). Violet is reserved for the sell
 * chrome + the drag-active state — never the file rows.
 */

import { cva } from 'class-variance-authority';

/** The drop surface. `variant` = size/layout, `state` = interaction. */
export const surfaceVariants = cva(
  'relative isolate overflow-hidden transition-colors outline-hidden focus-visible:ring-2 focus-visible:ring-ring/70',
  {
    variants: {
      // Box only (position / border / padding). Inner layout lives on the
      // content wrapper so the drag overlay can size to the box without the
      // surface changing dimensions mid-drag.
      variant: {
        banner: 'rounded-lg border-2 p-8',
        compact: 'rounded-lg border-2 p-4',
        mini: 'rounded-md border p-3'
      },
      state: {
        idle: 'border-border bg-muted/20 hover:border-muted-foreground/40 cursor-pointer',
        dragging: 'border-ai bg-ai/5 ring-1 ring-ai/25 cursor-copy',
        disabled: 'border-border bg-muted/20 opacity-50 cursor-not-allowed'
      },
      // A real drop target is dashed (the universal "drop here" affordance); a
      // non-droppable CTA (`dropzone={false}`) is solid so it doesn't promise a
      // drop the surface won't accept.
      droppable: {
        true: 'border-dashed',
        false: 'border-solid'
      }
    },
    defaultVariants: {
      variant: 'compact',
      state: 'idle',
      droppable: true
    }
  }
);

/**
 * The leading icon tile. `sell` → the Vale AI mark, bare; neutral → an `Upload`
 * icon on a muted grey tile. Size tracks the surface variant.
 *
 * **The sell state carries no tile fill (VALE-1251), and the neutral one still
 * does.** The violet "extraction" gradient was a ground for the lucide sparkle,
 * a light stroke drawing that needed something behind it to register against.
 * The Vale mark is three filled shapes that carry their own weight, so the
 * gradient read as a container around a logo rather than as a motif. The
 * neutral tile keeps its fill because `Upload` is still a stroke icon.
 *
 * The box itself stays (`size-*` and the radius), so removing the fill shifts
 * nothing around it: the mark keeps the same centre and the copy beside it does
 * not move.
 */
export const iconTileVariants = cva('grid shrink-0 place-items-center', {
  variants: {
    variant: {
      banner: 'rounded-xl',
      compact: 'rounded-lg',
      mini: 'rounded-md'
    },
    size: {
      banner: 'size-15',
      compact: 'size-11',
      mini: 'size-9'
    },
    sell: {
      true: 'text-ai-subtle-foreground',
      false: 'bg-muted text-muted-foreground'
    }
  },
  defaultVariants: {
    variant: 'compact',
    size: 'compact',
    sell: false
  }
});
